import React, { useContext, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { RecurlyProvider } from '@recurly/react-recurly';
import { ThemeProvider } from 'styled-components';
import nebulaTheme from '@nebula/theme';

import { publishBillDateChangeFailure } from '../../helpers/changeBillingDate/analytics';
import PaymentCaptureMethod from '../PaymentCaptureMethod';
import PaymentContextProvider, { PAYMENT_STAGES, PaymentContext } from './PaymentContextProvider';
import PaymentMethodChange from '../PaymentMethodChange';
import PaymentMethodAuthorization from '../PaymentMethodAuthorization';
import PaymentProcessing from '../PaymentProcessing';
import { getEnv } from '../Context/env';
import PaymentChangeAuthorization from '../PaymentChangeAuthorization';
import ExistingPaymentDetails from '../PaymentMethodChange/ExistingPaymentDetails';
import PaymentBackNavigation from '../PaymentBackNavigation/PaymentBackNavigation';
import { splitsList } from '../../constants';
import useSplits from '../../hooks/useSplits';
import PaymentMethodChangeV2 from '../PaymentMethodChangeV2/index.tsx';
import CPMSuccessStatus from '../../pages/payment-and-billing/change-payment-method/status/success.tsx';
import CPMErrorStatus from '../../pages/payment-and-billing/change-payment-method/status/error.tsx';
import ChangeBillingAuthorization from '../ChangeBillingDateAuthorization';
import ChangeBillingDateSuccess from '../../pages/change-billing-date/change-billing-date-success';
import ChangeBillingDateFailure from '../../pages/change-billing-date/change-billing-date-failure';
import Loader from '../../pages/change-billing-date/review-and-pay/loader';
import ChooseNewBillingDate from '../../pages/change-billing-date/choose-new-billing-date';
import { ReviewAndPayIndex } from '../../pages/change-billing-date/review-and-pay';

export const PaymentStages = () => {
  const paymentContext = useContext(PaymentContext);
  const { paymentStage, isWebview, tokens } = paymentContext;

  let stageComponent = null;

  switch (paymentStage) {
    case PAYMENT_STAGES.REQUIRES_PAYMENT_METHOD:
      stageComponent = <PaymentCaptureMethod />;
      break;
    case PAYMENT_STAGES.REQUIRES_AUTHORIZATION:
      stageComponent = <PaymentMethodAuthorization />;
      break;
    case PAYMENT_STAGES.REQUIRES_FULFILLMENT:
      stageComponent = <PaymentProcessing />;
      break;
    default:
      return null;
  }

  const userHasEnteredPaymentDetails = tokens?.paymentMethod || tokens?.isUsingExistingMethod;

  return (
    <React.Fragment>
      {!isWebview && userHasEnteredPaymentDetails && <PaymentBackNavigation />}
      {stageComponent}
    </React.Fragment>
  );
};

export const PaymentMethodUpdateStages = () => {
  const paymentContext = useContext(PaymentContext);
  const { paymentStage } = paymentContext;

  let stageComponent;

  switch (paymentStage) {
    case PAYMENT_STAGES.REQUIRES_PAYMENT_METHOD:
    case PAYMENT_STAGES.PAYMENT_METHOD_UPDATED:
      stageComponent = <PaymentMethodChange />;
      break;
    case PAYMENT_STAGES.REQUIRES_AUTHORIZATION:
      stageComponent = <PaymentChangeAuthorization />;
      break;
    default:
      return null;
  }

  return (
    <ThemeProvider theme={nebulaTheme}>
      <ExistingPaymentDetails />
      {stageComponent}
    </ThemeProvider>
  );
};

export const BillDateChangeStages = () => {
  const paymentContext = useContext(PaymentContext);
  const { paymentStage, isWebview } = paymentContext;
  const history = useHistory();
  const handleFailureAction = useCallback(() => {
    const billingPath = isWebview ? '/mobile/billing' : '/billing';
    history.replace(billingPath);
  }, [history, isWebview]);

  const amountPaid = useSelector(state => state.oneTimePayment?.price);

  let stageComponent;

  switch (paymentStage) {
    case PAYMENT_STAGES.CHOOSE_BILLING_DATE:
      stageComponent = <ChooseNewBillingDate />;
      break;
    case PAYMENT_STAGES.REVIEW_AND_PAY:
      stageComponent = <ReviewAndPayIndex />;
      break;
    case PAYMENT_STAGES.REQUIRES_AUTHORIZATION:
      stageComponent = <ChangeBillingAuthorization />;
      break;
    case PAYMENT_STAGES.CHANGE_BILLING_DATE_LOADING:
      stageComponent = <Loader />;
      break;
    case PAYMENT_STAGES.CHANGE_BILLING_DATE_PROCESSING:
      stageComponent = <Loader text="Processing payment" />;
      break;
    case PAYMENT_STAGES.CHANGE_BILLING_DATE_SUCCESS:
      stageComponent = <ChangeBillingDateSuccess />;
      break;
    case PAYMENT_STAGES.CHANGE_BILLING_DATE_FAILURE:
      stageComponent = (
        <ChangeBillingDateFailure
          onActionClick={handleFailureAction}
          onPageLoad={() => {
            publishBillDateChangeFailure({
              attemptedPaymentAmount: amountPaid,
            });
          }}
        />
      );
      break;
    default:
      return null;
  }

  return <ThemeProvider theme={nebulaTheme}>{stageComponent}</ThemeProvider>;
};

export const PaymentMethodUpdateStagesV2 = ({ toggleDrawer }) => {
  const paymentContext = useContext(PaymentContext);
  const { paymentStage } = paymentContext;

  let stageComponent;
  switch (paymentStage) {
    case PAYMENT_STAGES.REQUIRES_PAYMENT_METHOD:
      stageComponent = <PaymentMethodChangeV2 />;
      break;
    case PAYMENT_STAGES.PAYMENT_METHOD_UPDATED:
      stageComponent = <CPMSuccessStatus toggleHide={toggleDrawer} />;
      break;
    case PAYMENT_STAGES.SERVICE_UNAVAILABLE:
      stageComponent = <CPMErrorStatus />;
      break;
    case PAYMENT_STAGES.REQUIRES_AUTHORIZATION:
      stageComponent = <PaymentChangeAuthorization />;
      break;
    default:
      return null;
  }

  return <ThemeProvider theme={nebulaTheme}>{stageComponent}</ThemeProvider>;
};

PaymentMethodUpdateStagesV2.propTypes = {
  toggleDrawer: PropTypes.func,
};

const PaymentOrchestrator = ({
  upgradeOffer,
  upgradePlan,
  currentFlexSubscription,
  isTrial,
  isFlexFlow,
  isMethodChange,
  isBillDateChange,
  isBillingPeriodChange,
  targetBillingPeriod,
  isRestart,
  isWebview,
  toggleDrawer,
  provideContext = true,
}) => {
  const { REACT_APP_RECURLY_PUBLIC_KEY: key } = getEnv();
  const initialPaymentStage = isBillDateChange ? PAYMENT_STAGES.CHOOSE_BILLING_DATE : undefined;
  const billingInfoId = useSelector(state => state.billing?.card?.billingProviderPaymentMethodId);

  const recurlyConfig = useMemo(() => {
    const config = { publicKey: key };

    if (isBillDateChange && billingInfoId) {
      config.risk = {
        threeDSecure: {
          preflightDeviceDataCollector: {
            enabled: true,
            billingInfoId,
          },
        },
      };
    }

    return config;
  }, [key, billingInfoId, isBillDateChange]);
  const splits = useSplits([splitsList.webNewChangePaymentMethod]);
  const isWebNewChangePaymentMethodEnabled = splits[splitsList.webNewChangePaymentMethod];
  function getPaymentMethodComponent() {
    if (isBillDateChange) {
      return <BillDateChangeStages />;
    }
    if (!isMethodChange) {
      return <PaymentStages />;
    }
    return isWebNewChangePaymentMethodEnabled ? (
      <PaymentMethodUpdateStagesV2 toggleDrawer={toggleDrawer} />
    ) : (
      <PaymentMethodUpdateStages />
    );
  }

  const content = <RecurlyProvider {...recurlyConfig}>{getPaymentMethodComponent()}</RecurlyProvider>;

  return provideContext ? (
    <PaymentContextProvider
      upgradeOffer={upgradeOffer}
      upgradePlan={upgradePlan}
      currentFlexSubscription={currentFlexSubscription}
      isTrial={isTrial}
      isFlexFlow={isFlexFlow}
      isWebview={isWebview}
      isMethodChange={isMethodChange}
      isBillingPeriodChange={isBillingPeriodChange}
      targetBillingPeriod={targetBillingPeriod}
      isRestart={isRestart}
      initialPaymentStage={initialPaymentStage}
    >
      {content}
    </PaymentContextProvider>
  ) : (
    content
  );
};

PaymentOrchestrator.propTypes = {
  upgradeOffer: PropTypes.shape({
    family: PropTypes.string,
    name: PropTypes.string,
    upc: PropTypes.string,
    id: PropTypes.string,
    descriptors: PropTypes.arrayOf(PropTypes.string),
  }),
  upgradePlan: PropTypes.shape({
    family: PropTypes.string,
    product: PropTypes.shape({
      planCode: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      trialLength: PropTypes.number,
      term: PropTypes.string,
    }),
  }),
  currentFlexSubscription: PropTypes.shape({
    family: PropTypes.string,
    planCode: PropTypes.string,
    freeTrial: PropTypes.bool,
    billingFrequency: PropTypes.string,
  }),
  isMethodChange: PropTypes.bool,
  isBillDateChange: PropTypes.bool,
  isBillingPeriodChange: PropTypes.bool,
  targetBillingPeriod: PropTypes.oneOf(['monthly', 'annual']),
  isTrial: PropTypes.bool,
  isFlexFlow: PropTypes.bool,
  isWebview: PropTypes.bool,
  isRestart: PropTypes.bool,
  provideContext: PropTypes.bool,
  toggleDrawer: PropTypes.func,
};

PaymentOrchestrator.defaultProps = {
  upgradeOffer: null,
  upgradePlan: null,
  currentFlexSubscription: null,
  isMethodChange: false,
  isTrial: false,
  isFlexFlow: false,
  isWebview: false,
  isRestart: false,
  isBillDateChange: false,
  isBillingPeriodChange: false,
  provideContext: true,
};

export default PaymentOrchestrator;
